import type { ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { formatDate } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button, DataRecordCard } from '@/shared/ui';
import { SNAPCHAT_ADS_REPORTING_INTEGRATION_ID } from '../../model/constants';
import {
  deleteIntegration,
  selectIntegrations,
  selectIsLoading,
} from '../../model/slice';
import type {
  Datasource,
  SnapchatAdsReportingIntegration,
} from '../../model/types';
import { useURLSearchParamsBroadcaster } from '../../model/useURLSearchParamsBroadcaster';
import { DatasourcePanel } from '../DatasourcePanel/DatasourcePanel';
import { BROADCAST_CHANNEL_NAME } from './model/constants';
import { useSnapchatAuthCode } from './model/useSnapchatAuthCode';
import { SnapchatIcon } from './ui/SnapchatIcon/SnapchatIcon';

const SnapchatButton = styled((props: ButtonProps) => {
  return (
    <Button
      variant="outlined"
      color="secondary"
      startIcon={<SnapchatIcon />}
      {...props}
    >
      Add account
    </Button>
  );
})(({ theme }) => ({
  color: '#000000',
  height: 40,
  borderRadius: theme.shape.borderRadius * 2,
  fontSize: '0.75rem',
  lineHeight: '1.125rem',
})) as typeof Button;

interface Props {
  datasource: Datasource;
}

export function SnapchatAdsDatasource({ datasource }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const integrationsLoading = useAppSelector(selectIsLoading);
  const integrations = useAppSelector(selectIntegrations);
  const snapchatAdsReportingIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      return (
        integration.sourceIntegrationId ===
        SNAPCHAT_ADS_REPORTING_INTEGRATION_ID
      );
    }) as SnapchatAdsReportingIntegration[];
  }, [integrations]);
  const { processing, openDataAccessDialog } = useSnapchatAuthCode();

  useURLSearchParamsBroadcaster({
    channelName: BROADCAST_CHANNEL_NAME,
    params: ['code'],
  });

  const handleDeleteIntegration = (id: string) => () => {
    return dispatch(deleteIntegration(id))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Integration successfully deleted.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      })
      .catch(() => {
        enqueueSnackbar('Unable to delete integration. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      });
  };

  return (
    <DatasourcePanel
      loading={integrationsLoading || processing}
      datasource={datasource}
      addButtonSlot={<SnapchatButton onClick={openDataAccessDialog} />}
      integrationsSlot={snapchatAdsReportingIntegrations.map((integration) => {
        return (
          <DataRecordCard
            key={integration.id}
            recordNoun="integration"
            title={integration.integrationConfig.email}
            fields={[
              {
                label: 'Last update date',
                value: formatDate(integration.updatedAt),
              },
            ]}
            onDelete={handleDeleteIntegration(integration.id)}
          />
        );
      })}
    />
  );
}
